// Task notes format (week-one feedback §7): plain text, no editor, no new
// tables. Four line types are understood — `## Heading`, `- [ ]` / `- [x]`
// checkboxes, `-` bullets and `label = expression` sum lines — everything
// else is text and round-trips untouched. Kept pure and React-free so the
// panel, the row badge and the tests all read the same rules.

export type NotesLine =
  | { kind: 'heading'; index: number; raw: string; text: string }
  | { kind: 'check'; index: number; raw: string; text: string; checked: boolean }
  | { kind: 'bullet'; index: number; raw: string; text: string }
  | { kind: 'text'; index: number; raw: string; text: string }
  | {
      kind: 'calc';
      index: number;
      raw: string;
      label: string;
      expression: string;
      /** null when the expression didn't parse — render `raw` as plain text. */
      value: number | null;
      /** Formatted result, '' when `error`. */
      display: string;
      error: boolean;
    };

/** Notes bodies are a text column, not a document store — the panel and the
    server both refuse more than this. */
export const NOTES_MAX_LENGTH = 20000;

/** Request-side parse for a notes column, shared by the tasks and
    task-recurrence routes. A whitespace-only body is null; anything else is
    stored exactly as it was typed — the format is line-based, so a trailing
    blank line is content, not slack — up to the cap. */
export function parseNotesField(v: unknown): { notes: string | null; error?: string } {
  if (typeof v !== 'string' || v.trim() === '') return { notes: null };
  if (v.length > NOTES_MAX_LENGTH) {
    return {
      notes: null,
      error: `Notes must be ${NOTES_MAX_LENGTH.toLocaleString('en-AU')} characters or fewer.`,
    };
  }
  return { notes: v };
}

/** Windows line endings round-trip: lines are read without the \r and the
    text is re-joined with whatever separator it arrived with. */
const NEWLINE_RE = /\r?\n/;
const separatorOf = (text: string): string => (text.includes('\r\n') ? '\r\n' : '\n');

const HEADING_RE = /^\s*#{1,6}\s+(.*)$/;
// `* [ ]` is tolerated alongside `- [ ]`; the x is case-insensitive.
const CHECK_RE = /^\s*[-*]\s+\[([ xX])\]\s?(.*)$/;
const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const CALC_RE = /^([^=]+?)\s*=\s*(.+)$/;

/** Splits `text` into lines, preserving blank lines. */
export function parseNotes(text: string | null | undefined): NotesLine[] {
  if (!text) return [];
  return text.split(NEWLINE_RE).map((raw, index) => parseLine(raw, index));
}

function parseLine(raw: string, index: number): NotesLine {
  const heading = HEADING_RE.exec(raw);
  if (heading) return { kind: 'heading', index, raw, text: heading[1].trim() };

  const check = CHECK_RE.exec(raw);
  if (check) {
    return { kind: 'check', index, raw, text: check[2], checked: check[1].toLowerCase() === 'x' };
  }

  const bullet = BULLET_RE.exec(raw);
  if (bullet) return { kind: 'bullet', index, raw, text: bullet[1] };

  const calc = CALC_RE.exec(raw);
  if (calc && calc[1].trim() !== '') {
    const label = calc[1].trim();
    const expression = calc[2].trim();
    const value = evaluate(expression);
    if (value === null) {
      return { kind: 'calc', index, raw, label, expression, value: null, display: '', error: true };
    }
    const money = label.includes('$') || expression.includes('$');
    return {
      kind: 'calc',
      index,
      raw,
      label,
      expression,
      value,
      display: formatAmount(value, money),
      error: false,
    };
  }

  return { kind: 'text', index, raw, text: raw };
}

/** Flips the checkbox on one line and returns the whole text back, leaving
    every other line — and that line's own indent, bullet char and spacing —
    exactly as it was. A no-op if the line isn't a checkbox. */
export function toggleCheck(text: string, lineIndex: number): string {
  const lines = text.split(NEWLINE_RE);
  const line = lines[lineIndex];
  if (line === undefined) return text;
  const m = /^(\s*[-*]\s+\[)([ xX])(\][\s\S]*)$/.exec(line);
  if (!m) return text;
  lines[lineIndex] = `${m[1]}${m[2] === ' ' ? 'x' : ' '}${m[3]}`;
  return lines.join(separatorOf(text));
}

/** Checkbox tally for the row badge and the panel's progress line. */
export function checkProgress(text: string | null | undefined): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const line of parseNotes(text)) {
    if (line.kind !== 'check') continue;
    total += 1;
    if (line.checked) done += 1;
  }
  return { done, total };
}

// ── Expression evaluation ────────────────────────────────────────────────
// A tokenizer + two-level precedence walk, never eval/Function. Supports
// + − - × x * ÷ / over plain numbers, with a trailing % meaning "percent
// of" (so `14320 × 10%` is 1432).

type Token = { type: 'num'; value: number } | { type: 'op'; value: '+' | '-' | '*' | '/' };

const OPERATORS: Record<string, '+' | '-' | '*' | '/'> = {
  '+': '+',
  '-': '-',
  '−': '-', // − minus sign
  '–': '-', // – en dash
  '*': '*',
  '×': '*', // ×
  x: '*',
  X: '*',
  '/': '/',
  '÷': '/', // ÷
};

function tokenize(input: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === ' ' || ch === '\t' || ch === '$' || ch === ',') {
      i += 1;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < input.length && /[\d,]/.test(input[j])) j += 1;
      const whole = input.slice(i, j);
      // Either plain digits or proper thousands groups — "1,,2" is a typo,
      // not twelve.
      if (!/^\d+$/.test(whole) && !/^\d{1,3}(,\d{3})+$/.test(whole)) return null;
      if (input[j] === '.') {
        j += 1;
        const start = j;
        while (j < input.length && /\d/.test(input[j])) j += 1;
        if (j === start) return null;
      }
      let value = Number(input.slice(i, j).replace(/,/g, ''));
      if (!Number.isFinite(value)) return null;
      i = j;
      if (input[i] === '%') {
        value = value / 100;
        i += 1;
      }
      tokens.push({ type: 'num', value });
      continue;
    }
    const op = OPERATORS[ch];
    // A bare `x` only reads as multiply between two numbers, never inside a word.
    if (op && !((ch === 'x' || ch === 'X') && /[A-Za-z]/.test(input[i + 1] ?? ''))) {
      tokens.push({ type: 'op', value: op });
      i += 1;
      continue;
    }
    return null;
  }
  return tokens.length > 0 ? tokens : null;
}

/** Returns the expression's value, or null when it doesn't parse. */
export function evaluate(expression: string): number | null {
  const tokens = tokenize(expression);
  if (!tokens) return null;

  // Terms first (× ÷), then sum them (+ −) — one left-to-right pass each.
  const terms: number[] = [];
  const signs: ('+' | '-')[] = [];
  let cursor = 0;

  // A leading + / − signs the number rather than adding to it, so
  // "Balance = -500 + 200" reads the way it's typed.
  const readOperand = (): number | null => {
    let sign = 1;
    while (cursor < tokens.length) {
      const t = tokens[cursor];
      if (t.type !== 'op' || (t.value !== '-' && t.value !== '+')) break;
      if (t.value === '-') sign = -sign;
      cursor += 1;
    }
    const t = tokens[cursor];
    if (!t || t.type !== 'num') return null;
    cursor += 1;
    return sign * t.value;
  };

  const readTerm = (): number | null => {
    let acc = readOperand();
    if (acc === null) return null;
    while (cursor < tokens.length) {
      const op = tokens[cursor];
      if (op.type !== 'op' || (op.value !== '*' && op.value !== '/')) break;
      cursor += 1;
      const rhs = readOperand();
      if (rhs === null) return null;
      if (op.value === '/' && rhs === 0) return null;
      acc = op.value === '*' ? acc * rhs : acc / rhs;
    }
    return acc;
  };

  const first = readTerm();
  if (first === null) return null;
  terms.push(first);
  while (cursor < tokens.length) {
    const op = tokens[cursor];
    if (op.type !== 'op' || (op.value !== '+' && op.value !== '-')) return null;
    cursor += 1;
    const term = readTerm();
    if (term === null) return null;
    signs.push(op.value);
    terms.push(term);
  }

  let total = terms[0];
  for (let k = 1; k < terms.length; k += 1) {
    total = signs[k - 1] === '+' ? total + terms[k] : total - terms[k];
  }
  return Number.isFinite(total) ? total : null;
}

/** Money only when the line said so with a `$`; otherwise a plain number
    with thousands separators and up to 2 decimals. */
export function formatAmount(value: number, money: boolean): string {
  if (money) {
    // Same house style as formatMoneyExact (primitives.tsx) — "$1,432.00".
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 }).format(value);
}
